import L from 'leaflet'
import { createContext, ReactNode, useContext, useMemo, useState } from 'react'

import { useCompareMaps } from '@/contexts/compare-maps-context'
import { useGetDiagnosesCountByRegionId } from '@/hooks/queries/use-get-diagnoses-count-by-region-id'
import { useGetRegionById } from '@/hooks/queries/use-get-region-by-id'

import { useAutoSelectProjectRegion } from '../hooks/use-auto-select-project-region'
import { filterValidFields } from '../utils/field-validation'

export interface SelectedFields {
  id: string
  field_name: string
}

export interface FieldInfo {
  id: string
  field_name: string
  area: number
  diagnoses_count: number
  checked: boolean
}

export type RegionContextType = ReturnType<typeof useRegionContextHook>

const RegionContext = createContext({} as RegionContextType)

interface RegionProviderProps {
  children: ReactNode
}

const useRegionContextHook = () => {
  const [currentViewBounds, setCurrentViewBounds] =
    useState<L.LatLngBounds | null>(null)

  const { selectedProject, selectedRegion, setSelection } =
    useAutoSelectProjectRegion()

  const regionQuery = useGetRegionById(selectedRegion?.id, {
    enabled: !!selectedRegion?.id,
  })

  const { data: diagnosesCount } = useGetDiagnosesCountByRegionId({
    regionId: selectedRegion?.id,
  })

  const { selectedFieldIds, setSelectedFieldIds } = useCompareMaps()

  const regionFieldsInfo = useMemo(() => {
    const features = regionQuery.data?.geojson?.features
    if (!features) return []

    return features.map(
      ({ properties }) =>
        ({
          id: properties.id,
          field_name:
            properties[regionQuery.data!.col_name!] || 'Talhão sem nome',
          area: parseFloat(properties?.area?.toFixed(2)) || 0,
          checked: Boolean(selectedFieldIds.get(properties.id)),
          diagnoses_count:
            diagnosesCount?.demand_count_by_field[properties.id] || 0,
        } as FieldInfo),
    )
  }, [regionQuery.data, selectedFieldIds, diagnosesCount])

  const selectedFields: SelectedFields[] = filterValidFields(
    regionFieldsInfo,
  ).filter((field) => selectedFieldIds.get(field.id))

  const toggleFieldSelection = (fieldId: string) => {
    setSelectedFieldIds((prev) =>
      new Map(prev).set(fieldId, !prev.get(fieldId)),
    )
  }

  const multipleFieldsSelection = (fieldIds: string[]) => {
    if (!fieldIds.length) return
    setSelectedFieldIds((prev) => {
      const newMap = new Map(prev)
      fieldIds.forEach((id) => newMap.set(id, true))
      return newMap
    })
  }

  function clearSelectionFieldIds() {
    setSelectedFieldIds(new Map<string, boolean>())
  }

  return {
    selectedRegion,
    selectedProject,
    regionData: regionQuery.data,
    isLoadingRegion: regionQuery.isLoading || !selectedRegion,
    regionFieldsInfo,
    currentViewBounds,
    selectedFields,
    setSelection,
    toggleFieldSelection,
    multipleFieldsSelection,
    setCurrentViewBounds,
    clearSelectionFieldIds,
  }
}

export function RegionProvider({ children }: RegionProviderProps) {
  const contextValue = useRegionContextHook()

  return (
    <RegionContext.Provider value={contextValue}>
      {children}
    </RegionContext.Provider>
  )
}

export function useRegionContext() {
  return useContext(RegionContext)
}
